import { UploadOutlined } from '@ant-design/icons';
import { Col, Divider, Form, Row, Select, Upload } from 'antd5';
import { difference } from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePapaParse } from 'react-papaparse';

import { Button, ModalActionButtons } from 'components/ui';

import { type DataField, type EditableCsvDamageRow, type SchemaOptions } from '../../types';
import { STEPS } from '../common';
import { ErrorText, LabelText, RequiredIndicator } from '../common.style';
import { createEditableCsvRows, filterBlankCsvDamageRows } from '../validation';

export const computeAutoMap = (
  headerOptions: string[],
  dataFields: DataField[]
): Map<string, string> => {
  const assigned = new Set<string>();
  const map = new Map<string, string>();
  for (const { key, label } of dataFields) {
    const match = headerOptions.find(h => h === label && !assigned.has(h));
    if (match !== undefined) {
      map.set(key, match);
      assigned.add(match);
    }
  }
  return map;
};

type UploadFormProps = {
  schemaOptions: SchemaOptions[];
  dataFields: DataField[];
  setRows: (rows: EditableCsvDamageRow[]) => void;
  step: number;
  setStep: (step: number) => void;
  onClose: () => void;
  onClickPreview: () => void;
  getFieldsValue: (fields: string[] | true) => any;
  resetFields: (fields?: string[]) => void;
  fieldsHidden: boolean;
};

export const UploadForm = ({
  schemaOptions,
  dataFields,
  setRows,
  step,
  setStep,
  onClose,
  onClickPreview,
  getFieldsValue,
  resetFields,
  fieldsHidden,
}: UploadFormProps) => {
  const { t } = useTranslation();
  const [headerOptions, setHeaderOptions] = useState<string[]>([]);
  const [manualUsedHeaders, setManualUsedHeaders] = useState<string[] | null>(null);
  const [damageCount, setDamageCount] = useState<number>(0);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
  const [csvError, setCsvError] = useState<string | null>(null);
  const { readString } = usePapaParse();

  const autoMap = useMemo(
    () => computeAutoMap(headerOptions, dataFields ?? []),
    [headerOptions, dataFields]
  );

  const usedHeaderOptions = useMemo(
    () => (manualUsedHeaders !== null ? manualUsedHeaders : Array.from(autoMap.values())),
    [autoMap, manualUsedHeaders]
  );

  const handleSelectHeader = (): void => {
    setManualUsedHeaders(
      Object.values(getFieldsValue(dataFields?.map(({ key }) => key)))
        .filter(Boolean)
        .map(String)
    );
  };

  const resetForm = (): void => {
    resetFields();
    setManualUsedHeaders(null);
    setDamageCount(0);
    setRows([]);
    setCsvError(null);
    setStep(STEPS.upload);
  };

  const MAX_CSV_RECORDS = 5000;

  const handleUpload = async (info: any) => {
    const { file } = info;
    const f: File = file;
    const csvText = await f.text();

    const { data, meta } = readString(csvText, {
      header: true,
      skipEmptyLines: 'greedy',
    });
    const { fields } = meta;
    const parsedRows = filterBlankCsvDamageRows(data);

    if (!fields?.length) {
      resetFields();
      setHeaderOptions([]);
      setManualUsedHeaders(null);
      setDamageCount(0);
      setRows([]);
      setCsvError(null);
      setSubmitDisabled(true);
      setStep(STEPS.upload);
      return;
    }

    const recordCount = parsedRows.length;

    if (recordCount > MAX_CSV_RECORDS) {
      setCsvError(
        t('inspection.fileExceedsRecordLimit', {
          defaultValue:
            'File exceeds the {{limit}} record limit. Please divide it into smaller files for upload.',
          limit: MAX_CSV_RECORDS,
        })
      );
      setSubmitDisabled(true);
      setStep(STEPS.upload);
      return;
    }

    setCsvError(null);
    resetFields(dataFields?.map(({ key }) => key));
    setHeaderOptions(fields);
    setManualUsedHeaders(null);

    setDamageCount(recordCount);
    if (recordCount <= 0) {
      setRows([]);
      setCsvError(t('inspection.noCsvDamageRows', 'CSV contains no Damage rows to upload.'));
      setSubmitDisabled(true);
      setStep(STEPS.upload);
      return;
    }

    setRows(createEditableCsvRows(parsedRows));
    setStep(STEPS.mapping);
    setSubmitDisabled(false);
  };

  const availableHeaderOptions = useMemo(
    () =>
      difference(headerOptions, usedHeaderOptions).map((h: string) => ({
        label: h,
        value: h,
      })),
    [headerOptions, usedHeaderOptions]
  );

  return (
    <>
      <Form.Item
        hidden={fieldsHidden}
        label={t('settings.damageSchema', 'Damage Schema')}
        key="damageSchemaId"
        name="damageSchemaId"
        rules={[
          {
            required: true,
            message: t('inspection.pleaseSelectDamageSchema', 'Please select damage schema'),
            whitespace: true,
          },
        ]}
      >
        <Select
          placeholder={t('inspection.selectAssetType', 'Select Asset Type')}
          allowClear
          options={schemaOptions}
          disabled={schemaOptions.length < 2}
        />
      </Form.Item>

      {step === STEPS.upload && (
        <>
          <p>
            {t(
              'inspection.damageUploadDescription',
              'The damage details upload will create damages and corresponding bounding boxes in Horizon. Make sure pixel coordinates and dates are formatted the right way, and that sufficient damage attributes (optional) are added.'
            )}
          </p>
          <p>
            {t('inspection.clickToDownloadSample', 'Click to download a sample')}{' '}
            <a href="/csv/upload_damages_sample.csv" download>
              CSV
            </a>{' '}
            {t('inspection.or', 'or')}{' '}
            <a href="/xlsx/upload_damages_sample.xlsx" download>
              XLSX
            </a>{' '}
            {t(
              'inspection.sampleFileInstructions',
              'file with explanations and examples. You may use it as a template after deleting the existing entries. If you use the XLSX file, remember to convert it to CSV after completing entries.'
            )}
          </p>
          <p>
            {t('inspection.maxDamagesNote', {
              defaultValue:
                'Note: There is a maximum of {{limit}} damages per upload. If you are importing more than {{limit}} damages, split the upload into multiple CSV files.',
              limit: MAX_CSV_RECORDS,
            })}
          </p>
        </>
      )}
      {csvError && <ErrorText>{csvError}</ErrorText>}
      <Form.Item name="file" hidden={fieldsHidden}>
        <Upload
          accept={'.csv' /* TODO: xls? */}
          beforeUpload={() => false} // send false so it doesn't attempt to use antd upload.
          onChange={handleUpload}
          onRemove={resetForm}
          maxCount={1}
        >
          {step === STEPS.upload && (
            <Button
              disabled={!getFieldsValue(['damageSchemaId']).damageSchemaId}
              icon={<UploadOutlined />}
            >
              {t('inspection.uploadDamages', 'Upload Damages')}
            </Button>
          )}
        </Upload>
      </Form.Item>

      {step === STEPS.mapping && !!headerOptions.length && (
        <>
          <Divider />
          <p>
            {t(
              'inspection.selectSourceColumnForEachAttribute',
              'Select a source column for each attribute'
            )}
          </p>
          {(dataFields ?? []).map(({ key, label, required }) => (
            <Row key={key}>
              <Col flex="1" />
              <Col flex="auto">
                <LabelText>
                  {required && <RequiredIndicator>* </RequiredIndicator>}
                  {label}:
                </LabelText>
              </Col>
              <Col flex="50%">
                <Form.Item
                  key={key}
                  name={key}
                  initialValue={autoMap.get(key)}
                  rules={[
                    {
                      required,
                      message: t('inspection.fieldCannotBeEmpty', {
                        defaultValue: '{{field}} cannot be empty',
                        field: label,
                      }),
                      whitespace: true,
                    },
                  ]}
                >
                  <Select
                    placeholder={t('inspection.selectSourceColumn', 'Select Source Column')}
                    allowClear
                    options={availableHeaderOptions}
                    onChange={handleSelectHeader}
                  />
                </Form.Item>
              </Col>
              <Col flex="50px" />
            </Row>
          ))}
          <Divider />
          {damageCount > 0 && (
            <b>
              {t('inspection.willCreateDamages', {
                defaultValue: 'Will create: {{count}} damages',
                count: damageCount,
              })}
            </b>
          )}
        </>
      )}

      {!fieldsHidden && (
        <ModalActionButtons>
          <Button
            onClick={() => {
              if (step === STEPS.upload) {
                onClose();
              } else {
                resetForm();
              }
            }}
          >
            {step === STEPS.upload ? t('common.cancel', 'Cancel') : t('common.back', 'Back')}
          </Button>
          {step !== STEPS.upload && (
            <Button type="primary" onClick={onClickPreview} disabled={submitDisabled}>
              {t('inspection.preview', 'Preview')}
            </Button>
          )}
        </ModalActionButtons>
      )}
    </>
  );
};
